import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import DashboardLayout from '../../components/dashboard/DashboardLayout'
import Card from '../../components/dashboard/Card'
import LoadingSpinner from '../../components/LoadingSpinner'
import InviteMemberModal from '../../components/modals/InviteMemberModal'
import MemberDetailsDrawer from './MemberDetailsDrawer'
import RemoveMemberModal from '../../components/modals/RemoveMemberModal'
import { 
  Users,
  CalendarBlank,
  Info,
  DotsThree,
  Eye,
  CalendarPlus,
  UserPlus,
  UserSwitch,
  Trash
} from '@phosphor-icons/react'

// Tooltip component
function Tooltip({ text, children }) {
  return (
    <div className="relative group">
      {children}
      <div className="absolute right-0 top-8 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 shadow-lg">
        <div className="absolute -top-1 right-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
        {text}
      </div>
    </div>
  )
}

export default function AdminDashboard() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [stats, setStats] = useState({
    totalMembers: 0,
    averageAttendance: 0,
    activeMembers: 0,
    inactiveMembers: 0
  })
  const [events, setEvents] = useState([])
  const [topPerforming, setTopPerforming] = useState([])
  const [performingFilter, setPerformingFilter] = useState(1)
  const [recentMembers, setRecentMembers] = useState([])
  const [openMemberMenu, setOpenMemberMenu] = useState(null)
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 })
  const [donutHover, setDonutHover] = useState(null)
  const [showMemberDrawer, setShowMemberDrawer] = useState(false)
  const [selectedMemberId, setSelectedMemberId] = useState(null)
  const [showRemoveModal, setShowRemoveModal] = useState(false)
  const [memberToRemove, setMemberToRemove] = useState(null)

  useEffect(() => {
    loadDashboardData()
  }, [])

  useEffect(() => {
    if (!loading) {
      loadTopPerforming(performingFilter)
    }
  }, [performingFilter])

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 18) return 'Good afternoon'
    return 'Good evening'
  }

  const getTimeOfDay = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'morning'
    if (hour < 18) return 'afternoon'
    return 'evening'
  }

  const loadDashboardData = async () => {
    try {
      setLoading(true)

      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser) return

      const { data: userData } = await supabase
        .from('users')
        .select('name, org_id')
        .eq('id', authUser.id)
        .single()

      setUser(userData)

      const { count: totalMembers } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', userData.org_id)

      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

      const { data: activeRecords } = await supabase
        .from('attendance_records')
        .select('user_id')
        .gte('marked_at', thirtyDaysAgo.toISOString())

      const uniqueActiveUsers = new Set(activeRecords?.map(a => a.user_id) || [])
      const activeCount = uniqueActiveUsers.size
      const inactiveCount = (totalMembers || 0) - activeCount

      const now = new Date().toISOString().split('T')[0]

      const { data: pastEvents } = await supabase
        .from('events')
        .select('id')
        .eq('org_id', userData.org_id)
        .lte('start_date', now)

      let avgAttendance = 0

      if (pastEvents && pastEvents.length > 0 && totalMembers > 0) {
        let totalAttendanceRate = 0

        for (const event of pastEvents) {
          const { count: attendedCount } = await supabase
            .from('attendance_records')
            .select('*', { count: 'exact', head: true })
            .eq('event_id', event.id)

          const eventAttendanceRate = (attendedCount / totalMembers) * 100
          totalAttendanceRate += eventAttendanceRate
        }

        avgAttendance = Math.round(totalAttendanceRate / pastEvents.length)
      }

      setStats({
        totalMembers: totalMembers || 0,
        averageAttendance: avgAttendance,
        activeMembers: activeCount,
        inactiveMembers: inactiveCount
      })

      const today = new Date().toISOString().split('T')[0]

      const { data: upcomingEvents } = await supabase
        .from('events')
        .select('*')
        .eq('org_id', userData.org_id)
        .gte('start_date', today)
        .order('start_date', { ascending: true })
        .limit(5)

      const remainingSlots = 5 - (upcomingEvents?.length || 0)
      let pastEventsList = []

      if (remainingSlots > 0) {
        const { data } = await supabase
          .from('events')
          .select('*')
          .eq('org_id', userData.org_id)
          .lt('start_date', today)
          .order('start_date', { ascending: false })
          .limit(remainingSlots)

        pastEventsList = data || []
      }

      setEvents([...(upcomingEvents || []), ...pastEventsList])

      const { data: members } = await supabase
        .from('users')
        .select('id, name, email, phone, created_at, profile_photo_url, avatar_url, leadership_level, role')
        .eq('org_id', userData.org_id)
        .neq('role', 'org_admin')
        .order('created_at', { ascending: false })
        .limit(10)

      const membersWithSubgroups = await Promise.all(
        (members || []).map(async (member) => {
          const { data: groupMembership } = await supabase
            .from('group_members')
            .select('group_id, groups(name, depth)')
            .eq('user_id', member.id)

          const subgroup = groupMembership?.find(gm => gm.groups?.depth === 3)

          return {
            ...member,
            subgroup_name: subgroup?.groups?.name || 'Unassigned'
          }
        })
      )

      setRecentMembers(membersWithSubgroups)
      await loadTopPerforming(1)

    } catch (error) {
      console.error('Error loading dashboard:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadTopPerforming = async (depth) => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser) return

      const { data: userData } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', authUser.id)
        .single()

      const { data: groupsAtDepth } = await supabase
        .from('groups')
        .select('id, name')
        .eq('org_id', userData.org_id)
        .eq('depth', depth)

      if (!groupsAtDepth || groupsAtDepth.length === 0) {
        setTopPerforming([])
        return
      }

      const leadershipLevelMap = {
        1: 'team',
        2: 'group',
        3: 'subgroup'
      }
      const leadershipLevel = leadershipLevelMap[depth]

      const performanceData = await Promise.all(
        groupsAtDepth.map(async (group) => {
          const { data: groupMembers } = await supabase
            .from('group_members')
            .select('user_id, users(name, leadership_level)')
            .eq('group_id', group.id)

          const memberIds = groupMembers?.map(gm => gm.user_id) || []
          const leader = groupMembers?.find(gm => gm.users?.leadership_level === leadershipLevel)
          const leaderName = leader?.users?.name || 'No leader assigned'

          if (memberIds.length === 0) {
            return { ...group, attendanceRate: 0, totalMembers: 0, leaderName }
          }

          const today = new Date().toISOString().split('T')[0]
          const { data: pastEventsData } = await supabase
            .from('events')
            .select('id')
            .eq('org_id', userData.org_id)
            .lte('start_date', today)

          if (!pastEventsData || pastEventsData.length === 0) {
            return { ...group, attendanceRate: 0, totalMembers: memberIds.length, leaderName }
          }

          let totalActualAttendance = 0

          for (const event of pastEventsData) {
            const { count: attendedCount } = await supabase
              .from('attendance_records')
              .select('*', { count: 'exact', head: true })
              .eq('event_id', event.id)
              .in('user_id', memberIds)

            totalActualAttendance += attendedCount || 0
          }

          const totalPossibleAttendance = memberIds.length * pastEventsData.length
          const attendanceRate = totalPossibleAttendance > 0
            ? Math.round((totalActualAttendance / totalPossibleAttendance) * 100)
            : 0

          return {
            ...group,
            attendanceRate,
            totalMembers: memberIds.length,
            leaderName
          }
        })
      )

      const sortedPerformance = performanceData
        .sort((a, b) => b.attendanceRate - a.attendanceRate)
        .slice(0, 3)

      setTopPerforming(sortedPerformance)
    } catch (error) {
      console.error('Error loading top performing:', error)
    }
  }

  const getInitials = (name) => {
    if (!name) return '?'
    const parts = name.split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return name[0].toUpperCase()
  }

  const getAvatarColor = (id) => {
    const colors = [
      'bg-orange-400',
      'bg-blue-400',
      'bg-green-400',
      'bg-purple-400',
      'bg-pink-400',
      'bg-yellow-400',
      'bg-red-400',
      'bg-indigo-400',
      'bg-teal-400'
    ]
    
    const hash = id.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc)
    }, 0)
    
    return colors[Math.abs(hash) % colors.length]
  }

  const formatDate = (dateString, timeString) => {
    const date = new Date(dateString)
    const dateStr = date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric'
    })
    
    if (timeString) {
      return `${dateStr} ${timeString}`
    }
    return dateStr
  }

  const isUpcoming = (dateString) => {
    const today = new Date().toISOString().split('T')[0]
    return dateString >= today
  }

  const getMemberRoleBadge = (member) => {
    if (member.leadership_level === 'team') {
      return { text: 'Team lead', color: 'bg-[#eaf5ed] text-[#2b9646] border border-[#bfe0c8]' }
    }
    if (member.leadership_level === 'group') {
      return { text: 'Sub team head', color: 'bg-[#eaf0ff] text-[#2248b2] border border-[#adc2ff]' }
    }
    if (member.leadership_level === 'subgroup') {
      return { text: 'Department H.O.D', color: 'bg-[#f0eeff] text-[#493caf] border border-[#9589fc]' }
    }
    if (member.role === 'org_admin') {
      return { text: 'Organization Admin', color: 'bg-[#f8fafc] text-[#64748b] border border-[#e2e8f0]' }
    }
    if (member.role === 'unassigned' || !member.subgroup_name || member.subgroup_name === 'Unassigned') {
      return { text: 'New member', color: 'bg-[#f8fafc] text-[#64748b] border border-[#e2e8f0]' }
    }
    return { text: 'Member', color: 'bg-[#f8fafc] text-[#64748b] border border-[#e2e8f0]' }
  }

  if (loading) {
    return <LoadingSpinner message="Loading dashboard..." />
  }

  const activePercentage = stats.totalMembers > 0
    ? Math.round((stats.activeMembers / stats.totalMembers) * 100)
    : 0

  return (
    <DashboardLayout>
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {getGreeting()}, {user?.name?.split(' ')[0] || 'Admin'}
            </h1>
            <p className="text-gray-600 mt-1">How are you this {getTimeOfDay()}?</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Link
              to="/admin/events/create"
              className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              <CalendarPlus size={20} />
              Create event
            </Link>

            <button
              onClick={() => setShowInviteModal(true)}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              <UserPlus size={20} />
              Add member
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <div className="flex items-start justify-between mb-10">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Users size={20} className="text-blue-600" />
              </div>
              <Tooltip text="Total number of all members in your organization including administrators. This includes all active and inactive members.">
                <button className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                  <Info size={18} weight="fill" className="text-gray-400" />
                </button>
              </Tooltip>
            </div>
            <p className="text-8xl font-bold text-[#1E293B] mb-1">
              {stats.totalMembers}
            </p>
            <p className="text-base text-[#475569]">Total members</p>
          </Card>

          <Card>
            <div className="flex items-start justify-between mb-10">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <CalendarBlank size={20} className="text-blue-600" />
              </div>
              <Tooltip text="Average percentage of members who attended past events. Calculated by dividing actual attendance by total members for each event, then averaging across all past events.">
                <button className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                  <Info size={18} weight="fill" className="text-gray-400" />
                </button>
              </Tooltip>
            </div>
            <p className="text-8xl font-bold text-[#1E293B] mb-1">
              {stats.averageAttendance}%
            </p>
            <p className="text-base text-[#475569]">Average attendance rate</p>
          </Card>

          <Card>
            <div className="flex justify-end mb-2">
              <Tooltip text="Active members are those who have checked in to at least one event in the last 30 days. Inactive members have not checked in during this period.">
                <button className="p-1 hover:bg-gray-100 rounded-full transition-colors ">
                  <Info size={18} weight="fill" className="text-gray-500" />
                </button>
              </Tooltip>
            </div>
            <div className="flex flex-col items-center justify-center">
              <div className="relative w-40 h-40 mb-4">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    fill="none"
                    stroke="#D6E1FF"
                    strokeWidth="20"
                    className="cursor-pointer transition-opacity hover:opacity-80"
                    onMouseEnter={() => setDonutHover('inactive')}
                    onMouseLeave={() => setDonutHover(null)}
                  />
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    fill="none"
                    stroke="#3B82F6"
                    strokeWidth="20"
                    strokeDasharray={`${activePercentage * 4.4} ${(100 - activePercentage) * 4.4}`}
                    strokeLinecap="flat"
                    className="cursor-pointer transition-opacity hover:opacity-80"
                    onMouseEnter={() => setDonutHover('active')}
                    onMouseLeave={() => setDonutHover(null)}
                  />
                </svg>
                
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  {donutHover === 'active' ? (
                    <>
                      <p className="text-3xl font-bold text-blue-600">{stats.activeMembers}</p>
                      <p className="text-sm text-gray-600">Active</p>
                      <p className="text-xs text-gray-500">{activePercentage}%</p>
                    </>
                  ) : donutHover === 'inactive' ? (
                    <>
                      <p className="text-3xl font-bold text-gray-600">{stats.inactiveMembers}</p>
                      <p className="text-sm text-gray-600">Inactive</p>
                      <p className="text-xs text-gray-500">{100 - activePercentage}%</p>
                    </>
                  ) : (
                    <>
                      <p className="text-3xl font-bold text-gray-900">{stats.totalMembers}</p>
                      <p className="text-sm text-gray-600">Total members</p>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-600 rounded-sm"></div>
                  <span className="text-sm text-gray-600">Active ({stats.activeMembers})</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-[#D6E1FF] rounded-sm"></div>
                  <span className="text-sm text-gray-600">Inactive ({stats.inactiveMembers})</span>
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Events</h2>
              <Link
                to="/admin/events"
                className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
              >
                Go to events
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>

            <div className="space-y-3">
              {events.length === 0 ? (
                <p className="text-center py-8 text-gray-500 text-sm">No events yet</p>
              ) : (
                events.map((event) => (
                  <div
                    key={event.id}
                    className="flex items-start justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <img
                        src={event.banner_url || '/placeholder-event.jpg'}
                        alt={event.title}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{event.title}</p>
                        <p className="text-sm text-gray-500">{event.venue || 'No venue'}</p>
                        <p className="text-xs text-gray-400">
                          {formatDate(event.start_date, event.start_time)}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          isUpcoming(event.start_date)
                            ? 'bg-orange-50 text-orange-600'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {isUpcoming(event.start_date) ? 'Upcoming' : 'Past'}
                      </span>
                      <Link
                        to={`/admin/events/${event.id}`}
                        className="text-gray-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                      >
                        View
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Top performing by attendance</h2>
              <select
                value={performingFilter}
                onChange={(e) => setPerformingFilter(Number(e.target.value))}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={1}>Teams</option>
                <option value={2}>Sub teams</option>
                <option value={3}>Departments</option>
              </select>
            </div>

            <div className="space-y-10">
              {topPerforming.length === 0 ? (
                <p className="text-center py-8 text-gray-500 text-sm">No data available</p>
              ) : (
                topPerforming.map((item) => (
                  <div key={item.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-16 h-16 ${getAvatarColor(item.id)} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <span className="text-white font-bold text-2xl">
                          {getInitials(item.name)}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{item.name}</p>
                        <p className="text-sm text-gray-500">
                          Total member: {item.totalMembers}
                        </p>
                        <p className="text-xs text-gray-400">{item.leaderName}</p>
                      </div>
                    </div>
                    <p className="text-3xl font-medium text-gray-900">
                      {item.attendanceRate}%
                    </p>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>

        <Card title="Recent Members">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Full name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email address</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone number</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date joined</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentMembers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      No members yet
                    </td>
                  </tr>
                ) : (
                  recentMembers.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          {(member.profile_photo_url || member.avatar_url) ? (
                            <img
                              src={member.profile_photo_url || member.avatar_url}
                              alt={member.name}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className={`w-8 h-8 ${getAvatarColor(member.id)} rounded-full flex items-center justify-center`}>
                              <span className="text-white font-semibold text-sm">
                                {getInitials(member.name)}
                              </span>
                            </div>
                          )}
                          <span className="text-sm font-medium text-gray-900">
                            {member.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {member.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {member.phone || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(member.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getMemberRoleBadge(member).color}`}>
                          {getMemberRoleBadge(member).text}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="relative">
                          <button 
                            onClick={(e) => {
                              if (openMemberMenu === member.id) {
                                setOpenMemberMenu(null)
                              } else {
                                const rect = e.currentTarget.getBoundingClientRect()
                                const dropdownHeight = 130
                                const spaceBelow = window.innerHeight - rect.bottom
                                const spaceAbove = rect.top
                                
                                const showAbove = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight
                                
                                setMenuPosition({
                                  top: showAbove ? rect.top - dropdownHeight - 8 : rect.bottom + 8,
                                  right: window.innerWidth - rect.right
                                })
                                setOpenMemberMenu(member.id)
                              }
                            }}
                            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <DotsThree size={20} weight="bold" className="text-gray-500" />
                          </button>
                          
                          {openMemberMenu === member.id && (
                            <>
                              <div 
                                className="fixed inset-0 z-10" 
                                onClick={() => setOpenMemberMenu(null)}
                              />
                              <div 
                                className="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20 flex flex-col"
                                style={{
                                  top: `${menuPosition.top}px`,
                                  right: `${menuPosition.right}px`
                                }}
                              >
                                <button
                                  onClick={() => {
                                    setOpenMemberMenu(null)
                                    setSelectedMemberId(member.id)
                                    setShowMemberDrawer(true)
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                >
                                  <Eye size={16} />
                                  View details
                                </button>
                                <button
                                  onClick={() => {
                                    setOpenMemberMenu(null)
                                    navigate(`/admin/members/manage-role?memberId=${member.id}`)
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                >
                                  <UserSwitch size={16} />
                                  Manage role
                                </button>
                                <button
                                  onClick={() => {
                                    setOpenMemberMenu(null)
                                    setMemberToRemove(member)
                                    setShowRemoveModal(true)
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                                >
                                  <Trash size={16} />
                                  Remove member
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <InviteMemberModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={loadDashboardData}
      />

      <MemberDetailsDrawer
        isOpen={showMemberDrawer}
        onClose={() => {
          setShowMemberDrawer(false)
          setSelectedMemberId(null)
        }}
        memberId={selectedMemberId}
      />

      <RemoveMemberModal
        isOpen={showRemoveModal}
        onClose={() => {
          setShowRemoveModal(false)
          setMemberToRemove(null)
        }}
        member={memberToRemove}
        onSuccess={loadDashboardData}
      />
    </DashboardLayout>
  )
}